import { BlockLayoutType, ServerBlockTypeType } from "@pathwright/blocks-core"
import { scopeContentData } from "@pathwright/blocks-engine"
import PropTypes from "prop-types"
import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react"
import {
  createContext as createSelectorContext,
  useContextSelector
} from "use-context-selector"
import { getBlockType, getBlockTypeLabel, getStyles } from "../block/utils"
import { useBlocksConfig } from "../config/BlocksConfigProvider"
import {
  SyncedReducerReturnType,
  useSyncedReducer
} from "../syncer/useSyncedReducer"
import * as types from "../types"
import { generateBlockClip } from "../utils/utils"
import * as constants from "./constants"
import {
  EditorReducerActionType,
  EditorStateType,
  getInitialState,
  reducer
} from "./editorState"

// ESP = EditorStateProvider
export namespace ESPTypes {
  export type Props = {
    children: ReactNode
    syncer: (
      action: EditorReducerActionType,
      state: EditorStateType,
      dispatch: (action: EditorReducerActionType) => void
    ) => void
    content: types.BlocksContentType
    blockTypes: ServerBlockTypeType[]
    blocksContext: types.BlocksContextType
    contentLoading?: boolean
    needsContentReset?: boolean
  }

  export type Handlers = {
    handleBlockChange: (
      change: Partial<types.BlockDataType>,
      blockId: string
    ) => void
    handleToggleMode: () => void
    handlePublishContent: () => void
    handleDiscardDraftContent: () => void
    handleAddBlock: (
      type: string,
      order: number,
      data: types.BlockDataType,
      layout: string,
      style: types.BlockStyleType
    ) => void
    handleCopyBlock: (block?: types.BlockType | null) => void
    handlePasteBlock: (index: number, copies?: number) => Promise<void>
    handleMoveUp: (oldIndex: number, id: string) => void
    handleMoveDown: (oldIndex: number, id: string) => void
    handleBlockDelete: (blockId: string) => Promise<void>
    handleCancelBlockDelete: (blockIndex: number) => void
    handleLayoutChange: (
      blockId: string,
      layout: BlockLayoutType["key"]
    ) => void
    handleUpdateBlockStyles: (
      block: types.BlockType,
      style: types.BlockStyleType
    ) => void
  }
}

export type ComputedEditorContextValueType = {
  isPublished: boolean
}

type EditorStateContextValueType = EditorStateType &
  Omit<ESPTypes.Props, "children"> &
  ESPTypes.Handlers &
  ComputedEditorContextValueType

const EditorStateContext =
  createSelectorContext<EditorStateContextValueType | null>(null)
export const useEditorStateSelector = <T,>(
  selector: (state: EditorStateContextValueType) => T
): T =>
  useContextSelector(EditorStateContext, (state) =>
    selector(state || ({} as EditorStateContextValueType))
  )

export const EditorStateProvider = ({ children, ...props }: ESPTypes.Props) => {
  const { blockTypes, content, syncer, needsContentReset } = props

  const {
    copyBlock,
    copiedBlock: copiedBlockFromContext,
    contextKey
  } = useBlocksConfig()

  const [state, dispatch]: SyncedReducerReturnType<
    EditorStateType,
    EditorReducerActionType
  > = useSyncedReducer<
    EditorStateType,
    EditorReducerActionType,
    {
      content: types.BlocksContentType
      blockTypes: ServerBlockTypeType[]
    }
  >(reducer, syncer, { content, blockTypes }, getInitialState)

  // Debug the Editor state
  window.logContentState = useCallback(() => {
    const nextState = reducer(state, { type: constants.GET_STATE })
    const { blockTypes, ...logState } = nextState
    console.log("Editor state: ", JSON.stringify(logState, null, 2))
  }, [state])

  const contentRef = useRef(content)
  contentRef.current = content

  useEffect(() => {
    if (needsContentReset) {
      dispatch({
        type: constants.RESET,
        payload: {
          // Prefer ref so RESET sees content from this commit when the pulse
          // lands in the same turn as a content update (matches Viewer).
          content: contentRef.current,
          blockTypes
        }
      })
    }
  }, [needsContentReset, blockTypes])

  // Sync shared clipboard state from config into local editor state.
  // Run regardless of copyBlock so paste works even when this context has no copy UI.
  useEffect(() => {
    if (copiedBlockFromContext) {
      dispatch({
        type: constants.SET_COPIED_BLOCK,
        payload: {
          copiedBlockClip: copiedBlockFromContext
        }
      })
    } else {
      dispatch({ type: constants.CLEAR_COPIED_BLOCK })
    }
  }, [copiedBlockFromContext])

  // Respond to Apollo cache publish updates
  useEffect(() => {
    window.addEventListener("blocksPublished", (event: any) => {
      const { contextKey: eventKey } = event.detail

      if (eventKey !== contextKey) return

      dispatch({
        type: constants.SYNCED_PUBLISH,
        payload: content
      })
    })
  }, [])

  const handleToggleMode = useCallback((): void => {
    dispatch({ type: constants.TOGGLE_MODE })
  }, [])

  const handlePublishContent = useCallback(async (): Promise<void> => {
    dispatch({ type: constants.START_PUBLISH })
  }, [])

  const handleDiscardDraftContent = useCallback(async (): Promise<void> => {
    dispatch({ type: constants.START_DISCARD_DRAFT })
  }, [])

  // Pass style and data in case we're pasting a block
  const handleAddBlock = useCallback(
    (
      type: string,
      order: number,
      data: types.BlockDataType,
      layout: string,
      style?: types.BlockStyleType | undefined
    ): void => {
      // New flow: request create without optimistic block; show spinner
      dispatch({
        type: constants.ADD_BLOCK_REQUEST,
        payload: { type, order, data, layout, style }
      })
    },
    []
  )

  const handleCopyBlock = useCallback(
    (block?: types.BlockType | null): void => {
      // Clear copied block
      if (!block) {
        if (copyBlock) copyBlock(null)
        dispatch({ type: constants.CLEAR_COPIED_BLOCK })
        return
      }

      const { layout, type } = block

      // Generate a copied block clip
      const blockLabel: string = getBlockTypeLabel({ type, layout, blockTypes })
      const copiedBlockClip: types.CopiedBlockClipType = generateBlockClip(
        block,
        content.id,
        blockLabel
      )

      // Set the clip in state
      dispatch({
        type: constants.SET_COPIED_BLOCK,
        payload: {
          copiedBlockClip
        }
      })

      // Use clipboard copy function prop if available
      if (copyBlock) {
        return copyBlock(copiedBlockClip)
      }

      // If not clipboard is available, the fallback is to simply duplicate the block immediately below
      handleAddBlock(
        block.type,
        block.order + 1,
        scopeContentData(block.data, getBlockType(block.type, blockTypes)),
        block.layout,
        getStyles(block)
      )
    },
    []
  )

  const handlePasteBlock = useCallback(
    async (index: number = 0, copies?: number): Promise<void> => {
      let action: EditorReducerActionType

      if (copies && copies > 1) {
        action = {
          type: constants.PASTE_BLOCKS,
          payload: {
            index,
            copies
          }
        } as const
      } else {
        action = {
          type: constants.PASTE_BLOCK,
          payload: {
            index
          }
        } as const
      }

      dispatch(action)

      // // Clear the parent
      if (copyBlock) {
        copyBlock(null)
      }
    },
    []
  )

  const handleMoveUp = useCallback((oldIndex: number, id: string): void => {
    dispatch({
      type: constants.MOVE_BLOCK_UP,
      payload: {
        oldIndex,
        id
      }
    })
  }, [])

  const handleMoveDown = useCallback((oldIndex: number, id: string): void => {
    dispatch({
      type: constants.MOVE_BLOCK_DOWN,
      payload: {
        oldIndex,
        id
      }
    })
  }, [])

  const handleBlockDelete = useCallback(async (id: string): Promise<void> => {
    dispatch({ type: constants.DELETE_BLOCK, payload: { id } })
  }, [])

  const handleCancelBlockDelete = useCallback(() => {
    dispatch({ type: constants.CLEAR_DELETE_BLOCK })
  }, [])

  const handleDataChange = useCallback(
    (data: types.BlockDataType, id: string, trackProgress?: boolean) => {
      const action = {
        type: constants.UPDATE_BLOCK_DATA,
        payload: {
          id,
          block: {
            data
          },
          trackProgress
        }
      } as const

      dispatch(action)
    },
    []
  )

  const handleLayoutChange = useCallback(
    (id: string, layout: string) => {
      const action = {
        type: constants.UPDATE_BLOCK_LAYOUT,
        payload: {
          id,
          layout,
          blockTypes
        }
      } as const

      dispatch(action)
    },
    [blockTypes]
  )

  const handleUpdateBlockStyles = useCallback(
    (block: types.BlockType, style: types.BlockStyleType) => {
      const action = {
        type: constants.UPDATE_BLOCK_STYLE,
        payload: {
          id: block.id,
          style: style
        }
      } as const

      dispatch(action)
    },
    []
  )

  // Note: these handlers should have no dependencies and should access state via _getState()
  const handlers: ESPTypes.Handlers = useMemo(
    () => ({
      handleToggleMode,
      handlePublishContent,
      handleDiscardDraftContent,
      handleAddBlock,
      handleCopyBlock,
      handlePasteBlock,
      handleMoveUp,
      handleMoveDown,
      handleBlockDelete,
      handleCancelBlockDelete,
      handleBlockChange: handleDataChange,
      handleLayoutChange,
      handleUpdateBlockStyles
    }),
    []
  )

  const computed = useMemo(
    () => ({
      isPublished: state.lastPublished! >= state.lastModified!
    }),
    [state]
  )

  const value = useMemo(
    () => ({
      ...props,
      ...state,
      ...handlers,
      ...computed
    }),
    [props, state, handlers, computed]
  )

  return (
    <EditorStateContext.Provider value={value}>
      {children}
    </EditorStateContext.Provider>
  )
}

EditorStateProvider.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    lastModifiedDateTime: PropTypes.number,
    lastPublishedDateTime: PropTypes.number,
    blocks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
        data: PropTypes.object.isRequired
      })
    )
  }),
  blockTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      menu: PropTypes.shape({
        category: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired
      }).isRequired,
      layouts: PropTypes.objectOf(
        PropTypes.shape({
          data: PropTypes.object.isRequired,
          label: PropTypes.string.isRequired,
          key: PropTypes.string.isRequired
        })
      ),
      helpLink: PropTypes.string,
      defaults: PropTypes.object,
      __typename: PropTypes.string,
      fields: PropTypes.objectOf(
        PropTypes.shape({
          isInterfaceField: PropTypes.bool,
          isList: PropTypes.bool,
          name: PropTypes.string,
          type: PropTypes.string.isRequired,
          scope: PropTypes.string.isRequired,
          required: PropTypes.bool
        })
      )
    })
  ),
  blocksContext: PropTypes.shape({
    accountID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    userID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    accessToken: PropTypes.string,
    mediaStoragePath: PropTypes.string.isRequired
  }),
  syncer: PropTypes.func.isRequired
}

export default EditorStateProvider
